import { registerSW } from 'virtual:pwa-register'

const updateSW = registerSW({
  immediate: true,
  onNeedRefresh() {
    console.log('New content available, updating...')
    updateSW().then(() => {
      window.location.reload()
    })
  },
})

import { router } from './router'
import App from './App.vue'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/pl'
import { registerShortLocales } from '@/utils/dayjs-locales'
import vueDebounce from 'vue-debounce'
import { createApp } from 'vue'

import 'awesome-notifications/dist/style.css'

/* PrimeVue + PrimeIcons + PrimeFlex */
import 'primeicons/primeicons.css'

import PrimeVue from 'primevue/config'
import Aura from '@primeuix/themes/aura'
import { definePreset } from '@primeuix/themes'
import Tooltip from 'primevue/tooltip'
/* End Prime */

import '@/validation/index'
import vClickOutside from 'click-outside-vue3'
import '@/helpers/Analytics'
import '@/helpers/Extensions'
import { MutationCache, QueryClient, VueQueryPlugin } from 'vue-query'
import { createOnboardingMutationCacheOptions } from '@/vue-query/userOnboardingMutationInterceptor'

import { EasyToastPlugin } from '@/plugins/easy-toast/EasyToastPlugin'
import { PostHogPlugin } from '@/plugins/posthog/PostHogPlugin'
import { useEventBus } from './composables/useEventBus'
import { useDeviceInfo } from './composables/useDeviceInfo'
import { useInternationalization } from '@/i18n'
import Ripple from '@/directives/Ripple/RippleDirective'
import '@/directives/Ripple/styles.css'
import ConfirmationService from 'primevue/confirmationservice'
import 'shepherd.js/dist/css/shepherd.css'
import { setUpGA4Events } from '@/analytics'
import { createPinia } from 'pinia'
import 'floating-vue/dist/style.css'
import './styles.css'
import { App as CapacitorApp } from '@capacitor/app'
import { Browser } from '@capacitor/browser'
import { isEBEnv } from '@/helpers/env'

setUpGA4Events()

const pinia = createPinia()
const app = createApp(App)

app.use(pinia)
app.use(ConfirmationService)
app.directive('tooltip', Tooltip)
app.directive('ripple', Ripple)
app.use(useInternationalization())

app.use(EasyToastPlugin, {
  eventBus: useEventBus(),
  isMobile: useDeviceInfo().isMobile,
})

const queryClient = new QueryClient({
  mutationCache: new MutationCache(
    createOnboardingMutationCacheOptions(() => queryClient),
  ),
})

// If the environment is MOCK, skip query invalidation
// to prevent GET call again and test optimistic updates
if (isEBEnv('mock')) {
  queryClient.invalidateQueries = async () => {
    console.log('invalidateQueries was skipped')
    return Promise.resolve()
  }
}

app.use(VueQueryPlugin, {
  queryClient,
  queryClientConfig: {
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
      },
    },
  },
})

const preset = definePreset(Aura, {
  primitive: {
    red: {
      50: '#fdf2f2',
      100: '#f9e0e0',
      200: '#f2c0c2',
      300: '#e89a9d',
      400: '#db6e72',
      500: '#c9444d',
      600: '#a9383f',
      700: '#892d32',
      800: '#6a2327',
      900: '#4b191c',
      950: '#2d0f11',
    },
    green: {
      50: '#f0f7f2',
      100: '#dceede',
      200: '#b8dcc0',
      300: '#8cc69a',
      400: '#5aad6e',
      500: '#28a745',
      600: '#218c3a',
      700: '#1a7030',
      800: '#145525',
      900: '#0e3a1a',
      950: '#082210',
    },
    blue: {
      50: '#f0f4f8',
      100: '#d9e2ec',
      200: '#b8cce0',
      300: '#90b0d0',
      400: '#6890b8',
      500: '#4a72a0',
      600: '#3a5a88',
      700: '#2c4670',
      800: '#1e3050',
      900: '#152438',
      950: '#0c1520',
    },
    sky: {
      50: '#f0f4f8',
      100: '#d9e2ec',
      200: '#b8cce0',
      300: '#90b0d0',
      400: '#6890b8',
      500: '#4a72a0',
      600: '#3a5a88',
      700: '#2c4670',
      800: '#1e3050',
      900: '#152438',
      950: '#0c1520',
    },
    orange: {
      50: '#fef8f1',
      100: '#fdecd4',
      200: '#fbddb3',
      300: '#f9cc8e',
      400: '#f7ba62',
      500: '#ffab2b',
      600: '#e8962a',
      700: '#d08228',
      800: '#b06d24',
      900: '#8a5520',
      950: '#5e3a16',
    },
  },
  semantic: {
    primary: {
      50: '{orange.50}',
      100: '{orange.100}',
      200: '{orange.200}',
      300: '{orange.300}',
      400: '{orange.400}',
      500: '{orange.500}',
      600: '{orange.600}',
      700: '{orange.700}',
      800: '{orange.800}',
      900: '{orange.900}',
      950: '{orange.950}',
    },
    colorScheme: {
      light: {
        info: {
          50: '{blue.50}',
          100: '{blue.100}',
          200: '{blue.200}',
          300: '{blue.300}',
          400: '{blue.400}',
          500: '{blue.500}',
          600: '{blue.600}',
          700: '{blue.700}',
          800: '{blue.800}',
          900: '{blue.900}',
          950: '{blue.950}',
        },
        surface: {
          0: '#ffffff',
          50: '{slate.50}',
          100: '{slate.100}',
          200: '{slate.200}',
          300: '{slate.300}',
          400: '{slate.400}',
          500: '{slate.500}',
          600: '{slate.600}',
          700: '{slate.700}',
          800: '{slate.800}',
          900: '{slate.900}',
          950: '{slate.950}',
        },
      },
      dark: {
        info: {
          50: '{blue.50}',
          100: '{blue.100}',
          200: '{blue.200}',
          300: '{blue.300}',
          400: '{blue.400}',
          500: '{blue.500}',
          600: '{blue.600}',
          700: '{blue.700}',
          800: '{blue.800}',
          900: '{blue.900}',
          950: '{blue.950}',
        },
        surface: {
          0: '#ffffff',
          50: '#e6e7e8',
          100: '#cdcfd1',
          200: '#b4b6ba',
          300: '#9b9ea3',
          400: '#82868c',
          500: '#686e75',
          600: '#4f565e',
          700: '#0b213f',
          800: '#1d2530',
          900: '#071426',
          950: '#040d19',
        },
      },
    },
  },
  components: {
    button: {
      colorScheme: {
        light: {
          outlined: {
            contrast: {
              borderColor: '{surface.300}',
              color: '{surface.600}',
              hoverBackground: '{surface.50}',
              activeBackground: '{surface.100}',
            },
          },
        },
        dark: {
          outlined: {
            contrast: {
              borderColor: '{surface.600}',
              color: '{surface.200}',
              hoverBackground: '{surface.800}',
              activeBackground: '{surface.700}',
            },
          },
        },
      },
    },
  },
})

app.use(PrimeVue, {
  theme: {
    preset: preset,
    options: {
      darkModeSelector: '.app-dark',
    },
  },
  locale: {
    aria: {
      navigation: '',
    },
    startsWith: 'Starts with',
    contains: 'Contains',
    notContains: 'Not contains',
    endsWith: 'Ends with',
    equals: 'Equals',
    notEquals: 'Not equals',
    noFilter: 'No Filter',
    lt: 'Less than',
    lte: 'Less than or equal to',
    gt: 'Greater than',
    gte: 'Greater than or equal to',
    dateIs: 'Date is',
    dateIsNot: 'Date is not',
    dateBefore: 'Date is before',
    dateAfter: 'Date is after',
    clear: 'Wyczyść',
    apply: 'Apply',
    matchAll: 'Match All',
    matchAny: 'Match Any',
    addRule: 'Add Rule',
    removeRule: 'Remove Rule',
    accept: 'Tak',
    reject: 'Nie',
    choose: 'Choose',
    upload: 'Upload',
    cancel: 'Cancel',
    emptySearchMessage: 'Brak wyników',
    emptyMessage: 'Brak wyników',
    firstDayOfWeek: 1,
    dayNames: [
      'Niedziela',
      'Poniedziałek',
      'Wtorek',
      'Środa',
      'Czwartek',
      'Piątek',
      'Sobota',
    ],
    dayNamesShort: ['Niedz', 'Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob'],
    dayNamesMin: ['N', 'Pn', 'Wt', 'Śr', 'Czw', 'Pt', 'Sb'],
    monthNames: [
      'Styczeń',
      'Luty',
      'Marzec',
      'Kwiecień',
      'Maj',
      'Czerwiec',
      'Lipiec',
      'Sierpień',
      'Wrzesień',
      'Październik',
      'Listopad',
      'Grudzień',
    ],
    monthNamesShort: [
      'Sty',
      'Lut',
      'Mar',
      'Kwi',
      'Maj',
      'Cze',
      'Lip',
      'Sie',
      'Wrz',
      'Paź',
      'Lis',
      'Gru',
    ],
  },
})

app.use(PostHogPlugin, { router })

app.use(vClickOutside)

app.use(router)

app.use(vueDebounce, {
  lock: true,
  listenTo: ['input'],
  defaultTime: '700',
})

registerShortLocales()
dayjs.locale('pl')
dayjs.extend(duration)
dayjs.extend(relativeTime)
dayjs.duration({ months: 12 })

if (!isEBEnv('production')) {
  app.config.performance = true
}

app.mount('#app')

CapacitorApp.addListener('appUrlOpen', async ({ url }) => {
  const deepLink = new URL(url)

  const isCustomScheme = deepLink.protocol === 'easybudgetpl:'
  const path = isCustomScheme
    ? `/${deepLink.host}${deepLink.search}`
    : `${deepLink.pathname}${deepLink.search}`

  await router.isReady()
  await Browser.close()
  await router.replace(path || '/')
})
